import { useState, useEffect, useCallback } from 'react'
import { Loader2, Radar, Plus, X, Play, Clock, ChevronDown, Grid3X3 } from 'lucide-react'
import { useApi, asArray } from '../../hooks/useApi'
import { apiPost } from '../../lib/auth'

interface MapsProps {
  locationId: number | null
}

interface ScanSummary {
  id: number
  location_id: number
  grid_size: number
  radius_km: number
  keywords: string[]
  status: 'pending' | 'running' | 'completed' | 'failed'
  progress: number
  created_at: string
}

interface ScanPoint {
  row: number
  col: number
  keyword: string
  rank: number | null
  lat: number
  lng: number
}

interface ScanDetail {
  id: number
  grid_size: number
  radius_km: number
  keywords: string[]
  status: string
  progress: number
  points: ScanPoint[]
  created_at: string
}

interface PollResponse {
  status: string
  progress: number
}

type GridPreset = 3 | 5 | 7
type RadiusOption = 2 | 5 | 10 | 20

const GRID_PRESETS: { value: GridPreset; label: string; desc: string }[] = [
  { value: 3, label: '3 \u00d7 3', desc: '9 points \u2014 rapide' },
  { value: 5, label: '5 \u00d7 5', desc: '25 points \u2014 \u00e9quilibr\u00e9' },
  { value: 7, label: '7 \u00d7 7', desc: '49 points \u2014 pr\u00e9cis' },
]

const RADIUS_OPTIONS: RadiusOption[] = [2, 5, 10, 20]

function rankColor(rank: number | null): string {
  if (rank === null || rank > 20) return '#EF4444'
  if (rank <= 3) return '#22C55E'
  if (rank <= 10) return '#F59E0B'
  return '#F97316'
}

function rankBg(rank: number | null): string {
  if (rank === null || rank > 20) return 'bg-red-500/15'
  if (rank <= 3) return 'bg-emerald-500/15'
  if (rank <= 10) return 'bg-amber-400/15'
  return 'bg-orange-400/15'
}

function rankText(rank: number | null): string {
  if (rank === null || rank > 20) return '-'
  return `#${rank}`
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// ─── HeatmapGrid ──────────────────────────────────────────────

function HeatmapGrid({
  points,
  gridSize,
  keyword,
}: {
  points: ScanPoint[]
  gridSize: number
  keyword: string
}) {
  const filtered = points.filter((p) => p.keyword === keyword)

  // Build grid matrix
  const grid: (ScanPoint | null)[][] = Array.from({ length: gridSize }, () =>
    Array.from({ length: gridSize }, () => null)
  )
  filtered.forEach((p) => {
    if (p.row >= 0 && p.row < gridSize && p.col >= 0 && p.col < gridSize) {
      grid[p.row][p.col] = p
    }
  })

  const centerIdx = Math.floor(gridSize / 2)

  return (
    <div
      className="inline-grid gap-1.5"
      style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}
    >
      {grid.map((row, ri) =>
        row.map((cell, ci) => {
          const isCenter = ri === centerIdx && ci === centerIdx
          return (
            <div
              key={`${ri}-${ci}`}
              className={`w-12 h-12 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${rankBg(
                cell?.rank ?? null
              )} ${isCenter ? 'ring-2 ring-[#6366F1] ring-offset-1' : ''}`}
              style={{ color: rankColor(cell?.rank ?? null) }}
              title={
                cell
                  ? `Position ${cell.rank ?? 'absente'} (${cell.lat.toFixed(4)}, ${cell.lng.toFixed(4)})`
                  : ''
              }
            >
              {rankText(cell?.rank ?? null)}
            </div>
          )
        })
      )}
    </div>
  )
}

// ─── KeywordStats ──────────────────────────────────────────────

function KeywordStats({ points, keywords }: { points: ScanPoint[]; keywords: string[] }) {
  const stats = keywords.map((kw) => {
    const kwPoints = points.filter((p) => p.keyword === kw)
    const ranks = kwPoints.map((p) => p.rank).filter((r): r is number => r !== null && r <= 20)
    const avg = ranks.length > 0 ? ranks.reduce((s, v) => s + v, 0) / ranks.length : null
    const best = ranks.length > 0 ? Math.min(...ranks) : null
    const worst = ranks.length > 0 ? Math.max(...ranks) : null
    return { keyword: kw, avg, best, worst }
  })

  return (
    <div className="bg-white border border-[#E2E4ED] rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#E2E4ED] bg-[#F4F5FA]">
            <th className="text-left px-4 py-3 text-xs font-medium text-[#8B8FA7] uppercase tracking-wider">
              Mot-cl&eacute;
            </th>
            <th className="text-center px-4 py-3 text-xs font-medium text-[#8B8FA7] uppercase tracking-wider">
              Rang moyen
            </th>
            <th className="text-center px-4 py-3 text-xs font-medium text-[#8B8FA7] uppercase tracking-wider">
              Meilleur
            </th>
            <th className="text-center px-4 py-3 text-xs font-medium text-[#8B8FA7] uppercase tracking-wider">
              Pire
            </th>
          </tr>
        </thead>
        <tbody>
          {stats.map((s) => (
            <tr key={s.keyword} className="border-b border-[#E2E4ED] last:border-0 hover:bg-[#F4F5FA]/50">
              <td className="px-4 py-3 text-[#1B2559] font-medium">{s.keyword}</td>
              <td className="px-4 py-3 text-center">
                {s.avg !== null ? (
                  <span
                    className="inline-block px-2 py-0.5 rounded-md text-xs font-bold"
                    style={{
                      color: rankColor(Math.round(s.avg)),
                      backgroundColor: `${rankColor(Math.round(s.avg))}15`,
                    }}
                  >
                    {s.avg.toFixed(1)}
                  </span>
                ) : (
                  <span className="text-[#8B8FA7]">-</span>
                )}
              </td>
              <td className="px-4 py-3 text-center">
                {s.best !== null ? (
                  <span className="text-emerald-500 font-mono font-bold">#{s.best}</span>
                ) : (
                  <span className="text-[#8B8FA7]">-</span>
                )}
              </td>
              <td className="px-4 py-3 text-center">
                {s.worst !== null ? (
                  <span
                    className="font-mono font-bold"
                    style={{ color: rankColor(s.worst) }}
                  >
                    #{s.worst}
                  </span>
                ) : (
                  <span className="text-[#8B8FA7]">-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Main Maps component ──────────────────────────────────────

export default function Maps({ locationId }: MapsProps) {
  // Config state
  const [gridSize, setGridSize] = useState<GridPreset>(5)
  const [radius, setRadius] = useState<RadiusOption>(5)
  const [keywords, setKeywords] = useState<string[]>([])
  const [kwInput, setKwInput] = useState('')
  const [launching, setLaunching] = useState(false)

  // Active scan state
  const [activeScanId, setActiveScanId] = useState<number | null>(null)
  const [polling, setPolling] = useState(false)
  const [pollData, setPollData] = useState<PollResponse | null>(null)
  const [elapsed, setElapsed] = useState(0)

  // Results
  const [selectedKeyword, setSelectedKeyword] = useState<string>('')
  const [historyOpen, setHistoryOpen] = useState(false)

  // API: list scans
  const { data: scans, loading: scansLoading, refetch: refetchScans } = useApi<ScanSummary[]>(
    locationId ? `/api/local/locations/${locationId}/maps/scans` : ''
  )

  // API: scan detail
  const { data: scanDetail, loading: detailLoading, refetch: refetchDetail } = useApi<ScanDetail>(
    activeScanId ? `/api/local/maps/scans/${activeScanId}` : ''
  )

  const scansList = asArray(scans)

  // Auto-select latest completed scan
  useEffect(() => {
    if (activeScanId) return
    const completed = scansList.filter((s) => s.status === 'completed')
    if (completed.length > 0) {
      setActiveScanId(completed[0].id)
    }
    const running = scansList.find((s) => s.status === 'running' || s.status === 'pending')
    if (running) {
      setActiveScanId(running.id)
      setPolling(true)
    }
  }, [scans])

  // Auto-select first keyword when detail loads
  useEffect(() => {
    if (scanDetail?.keywords?.length && !selectedKeyword) {
      setSelectedKeyword(scanDetail.keywords[0])
    }
  }, [scanDetail])

  // Polling for running scan
  useEffect(() => {
    if (!polling || !activeScanId) return
    let cancelled = false
    const timer = setInterval(async () => {
      try {
        const res = await fetch(`/api/local/maps/scans/${activeScanId}/poll`, {
          credentials: 'include',
        })
        const json = await res.json()
        if (cancelled) return
        const pd = json.data || json
        setPollData(pd)
        if (pd.status === 'completed' || pd.status === 'failed') {
          setPolling(false)
          refetchDetail()
          refetchScans()
        }
      } catch {
        // ignore polling errors
      }
    }, 4000)

    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [polling, activeScanId])

  // Elapsed timer
  useEffect(() => {
    if (!polling) {
      setElapsed(0)
      return
    }
    const timer = setInterval(() => setElapsed((e) => e + 1), 1000)
    return () => clearInterval(timer)
  }, [polling])

  // ─── Handlers ─────────────────────────────────────────────

  const addKeyword = useCallback(() => {
    const kw = kwInput.trim().toLowerCase()
    if (kw && !keywords.includes(kw)) {
      setKeywords((prev) => [...prev, kw])
    }
    setKwInput('')
  }, [kwInput, keywords])

  const removeKeyword = (kw: string) => {
    setKeywords((prev) => prev.filter((k) => k !== kw))
  }

  const handleLaunch = async () => {
    if (!locationId || keywords.length === 0 || launching) return
    setLaunching(true)
    try {
      const { data } = await apiPost<{ scanId: number }>(
        `/api/local/locations/${locationId}/maps/scans`,
        { gridSize: `${gridSize}x${gridSize}`, radiusKm: radius, keywords }
      )
      if (data) {
        setActiveScanId(data.scanId)
        setPolling(true)
        setPollData({ status: 'running', progress: 0 })
        refetchScans()
      }
    } finally {
      setLaunching(false)
    }
  }

  const selectScan = (scanId: number) => {
    setActiveScanId(scanId)
    setSelectedKeyword('')
    setHistoryOpen(false)
    setPolling(false)
    setPollData(null)
  }

  // ─── Render ─────────────────────────────────────────────

  if (!locationId) {
    return (
      <div className="text-center py-20 text-[#555A73] text-sm">
        S&eacute;lectionnez un &eacute;tablissement pour scanner Maps.
      </div>
    )
  }

  if (scansLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#6366F1]" />
      </div>
    )
  }

  const isRunning = polling || pollData?.status === 'running' || pollData?.status === 'pending'
  const currentProgress = pollData?.progress ?? 0
  const hasCompleted = scanDetail?.status === 'completed' && !isRunning
  const showConfig = scansList.length === 0 || (!isRunning && !detailLoading)

  return (
    <div className="space-y-6">
      {/* History dropdown + New scan button */}
      {scansList.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-3">
          {/* History selector */}
          <div className="relative">
            <button
              onClick={() => setHistoryOpen((v) => !v)}
              className="flex items-center gap-2 px-3.5 py-2 bg-[#F4F5FA] border border-[#E2E4ED] rounded-xl text-sm text-[#1B2559] hover:border-[#C8CBD8] transition-all"
            >
              <Clock className="w-4 h-4 text-[#8B8FA7]" strokeWidth={1.25} />
              <span className="max-w-[200px] truncate">
                {activeScanId
                  ? `Scan du ${formatDateShort(scansList.find((s) => s.id === activeScanId)?.created_at || '')}`
                  : 'Historique'}
              </span>
              <ChevronDown className="w-4 h-4 text-[#555A73]" strokeWidth={1.25} />
            </button>
            {historyOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setHistoryOpen(false)} />
                <div className="absolute left-0 top-full mt-1.5 w-72 bg-white border border-[#E2E4ED] rounded-xl shadow-xl z-50 overflow-hidden max-h-60 overflow-y-auto">
                  {scansList.map((scan) => (
                    <button
                      key={scan.id}
                      onClick={() => selectScan(scan.id)}
                      className={`w-full text-left px-4 py-3 hover:bg-[#F4F5FA] transition-colors ${
                        scan.id === activeScanId ? 'bg-[#6366F1]/5' : ''
                      }`}
                    >
                      <p className="text-sm text-[#1B2559]">{formatDateShort(scan.created_at)}</p>
                      <p className="text-xs text-[#8B8FA7]">
                        {scan.grid_size}&times;{scan.grid_size} &middot; {scan.radius_km}km &middot;{' '}
                        {scan.keywords.length} mot(s)-cl&eacute;(s) &middot;{' '}
                        <span
                          className={
                            scan.status === 'completed'
                              ? 'text-emerald-500'
                              : scan.status === 'failed'
                              ? 'text-red-500'
                              : 'text-amber-500'
                          }
                        >
                          {scan.status}
                        </span>
                      </p>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* New scan button */}
          {!isRunning && (
            <button
              onClick={() => {
                setActiveScanId(null)
                setPollData(null)
                setSelectedKeyword('')
              }}
              className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] text-white text-sm font-medium rounded-xl hover:bg-[#5558E6] transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nouveau scan
            </button>
          )}
        </div>
      )}

      {/* Running scan — progress */}
      {isRunning && (
        <div className="bg-white border border-[#E2E4ED] rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#6366F1]/10 flex items-center justify-center">
              <Radar className="w-5 h-5 text-[#6366F1] animate-pulse" strokeWidth={1.25} />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#1B2559]">Scan en cours&hellip;</p>
              <p className="text-xs text-[#8B8FA7]">
                <Clock className="w-3 h-3 inline mr-1" />
                {formatElapsed(elapsed)}
              </p>
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs text-[#555A73]">
              <span>Progression</span>
              <span>{currentProgress}%</span>
            </div>
            <div className="h-2.5 bg-[#F4F5FA] rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#6366F1] to-[#A855F7] rounded-full transition-all duration-500"
                style={{ width: `${currentProgress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Config section */}
      {showConfig && !activeScanId && (
        <div className="bg-white border border-[#E2E4ED] rounded-xl p-6 space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#6366F1]/10 flex items-center justify-center">
              <Grid3X3 className="w-5 h-5 text-[#6366F1]" strokeWidth={1.25} />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#1B2559]">Configurer le scan Maps</p>
              <p className="text-xs text-[#8B8FA7]">
                D&eacute;finissez la grille, le rayon et les mots-cl&eacute;s &agrave; scanner.
              </p>
            </div>
          </div>

          {/* Grid presets */}
          <div>
            <label className="block text-xs font-medium text-[#555A73] uppercase tracking-wider mb-2">
              Taille de la grille
            </label>
            <div className="flex gap-3">
              {GRID_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => setGridSize(preset.value)}
                  className={`flex-1 px-4 py-3 rounded-xl border text-left transition-all ${
                    gridSize === preset.value
                      ? 'border-[#6366F1] bg-[#6366F1]/5'
                      : 'border-[#E2E4ED] hover:border-[#C8CBD8]'
                  }`}
                >
                  <p
                    className={`text-sm font-semibold ${
                      gridSize === preset.value ? 'text-[#6366F1]' : 'text-[#1B2559]'
                    }`}
                  >{preset.label}</p>
                  <p
                    className="text-xs text-[#8B8FA7] mt-0.5"
                  >{preset.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Radius */}
          <div>
            <label className="block text-xs font-medium text-[#555A73] uppercase tracking-wider mb-2">
              Rayon
            </label>
            <select
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value) as RadiusOption)}
              className="w-full max-w-xs px-3.5 py-2.5 bg-[#F4F5FA] border border-[#E2E4ED] rounded-xl text-sm text-[#1B2559] focus:outline-none focus:border-[#6366F1]"
            >
              {RADIUS_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r} km
                </option>
              ))}
            </select>
          </div>

          {/* Keywords */}
          <div>
            <label className="block text-xs font-medium text-[#555A73] uppercase tracking-wider mb-2">
              Mots-cl&eacute;s
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {keywords.map((kw) => (
                <span
                  key={kw}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#6366F1]/10 text-[#6366F1] text-sm font-medium rounded-lg"
                >
                  {kw}
                  <button
                    onClick={() => removeKeyword(kw)}
                    className="hover:text-[#4F46E5] transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={kwInput}
                onChange={(e) => setKwInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addKeyword()
                  }
                }}
                placeholder="ex: plombier strasbourg"
                className="flex-1 max-w-sm px-3.5 py-2.5 bg-[#F4F5FA] border border-[#E2E4ED] rounded-xl text-sm text-[#1B2559] placeholder:text-[#C8CBD8] focus:outline-none focus:border-[#6366F1]"
              />
              <button
                onClick={addKeyword}
                disabled={!kwInput.trim()}
                className="px-3.5 py-2.5 bg-[#F4F5FA] border border-[#E2E4ED] rounded-xl text-sm text-[#555A73] hover:border-[#C8CBD8] transition-colors disabled:opacity-40"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Launch */}
          <button
            onClick={handleLaunch}
            disabled={keywords.length === 0 || launching}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#6366F1] text-white text-sm font-medium rounded-xl hover:bg-[#5558E6] transition-colors disabled:opacity-50"
          >
            {launching ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            Lancer le scan
          </button>
        </div>
      )}

      {/* Results */}
      {hasCompleted && scanDetail && (
        <div className="space-y-6">
          {/* Keyword selector */}
          {scanDetail.keywords.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {scanDetail.keywords.map((kw) => (
                <button
                  key={kw}
                  onClick={() => setSelectedKeyword(kw)}
                  className={`px-3.5 py-2 text-sm font-medium rounded-xl border transition-all ${
                    selectedKeyword === kw
                      ? 'border-[#6366F1] bg-[#6366F1]/10 text-[#6366F1]'
                      : 'border-[#E2E4ED] text-[#555A73] hover:border-[#C8CBD8]'
                  }`}
                >
                  {kw}
                </button>
              ))}
            </div>
          )}

          {/* Heatmap + avg rank */}
          {selectedKeyword && (
            <div className="bg-white border border-[#E2E4ED] rounded-xl p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="text-sm font-semibold text-[#1B2559]">
                    Grille de positions &mdash; &laquo;&nbsp;{selectedKeyword}&nbsp;&raquo;
                  </p>
                  <p className="text-xs text-[#8B8FA7] mt-0.5">
                    {scanDetail.grid_size}&times;{scanDetail.grid_size} &middot; rayon {scanDetail.radius_km}km
                  </p>
                </div>
                {(() => {
                  const pts = asArray(scanDetail.points).filter(
                    (p) => p.keyword === selectedKeyword
                  )
                  const ranks = pts
                    .map((p) => p.rank)
                    .filter((r): r is number => r !== null && r <= 20)
                  const avg =
                    ranks.length > 0
                      ? (ranks.reduce((s, v) => s + v, 0) / ranks.length).toFixed(1)
                      : null
                  return avg ? (
                    <div className="text-right">
                      <p className="text-3xl font-mono font-bold text-[#1B2559]">{avg}</p>
                      <p className="text-xs text-[#8B8FA7]">rang moyen</p>
                    </div>
                  ) : null
                })()}
              </div>

              <div className="flex justify-center">
                <HeatmapGrid
                  points={asArray(scanDetail.points)}
                  gridSize={scanDetail.grid_size}
                  keyword={selectedKeyword}
                />
              </div>

              {/* Legend */}
              <div className="flex items-center justify-center gap-4 mt-6 text-xs text-[#555A73]">
                <span className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-emerald-500/15 border border-emerald-500/30" />
                  1-3
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-amber-400/15 border border-amber-400/30" />
                  4-10
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-orange-400/15 border border-orange-400/30" />
                  11-20
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-red-500/15 border border-red-500/30" />
                  Absent
                </span>
              </div>
            </div>
          )}

          {/* Stats table */}
          <KeywordStats points={asArray(scanDetail.points)} keywords={scanDetail.keywords} />
        </div>
      )}

      {/* Detail loading */}
      {detailLoading && activeScanId && !isRunning && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#6366F1]" />
        </div>
      )}
    </div>
  )
}
